import { hashFnv1a } from '../rng';
import { LEAGUE_DEFS } from '../race/params';
import type { StatId } from '../race/types';
import {
  ENTRY_HORIZON_DAYS,
  FEED_PLANS,
  LEAGUE_ECONOMY,
  eventDay,
  eventId,
  eventLeague,
  goingOf,
  raceClassOf,
  racesOnDay,
  type GoingId,
  type SurfaceId,
} from './economy';
import { ageOf, seasonDayOf, seasonOf, xpToNext } from './horse';
import type { RanchSim } from './sim';

export interface StatView {
  level: number;
  xp: number;
  xpToNext: number;
  cap: number;
}

export interface HudSnapshot {
  day: number;
  season: number;
  seasonDay: number;
  moneyCents: number;
  energy: number;
  energyMax: number;
  feedPlanName: string;
  hasHorse: boolean;
  horseName: string;
  horseAge: number | null;
  fatigue: number;
  /** Exact number only once the vet is built. */
  fatigueVisible: boolean;
  injuryDays: number;
  stats: Record<StatId, StatView>;
  winsByLeague: [number, number, number, number];
  starts: number;
  earningsCents: number;
  /** Today's entered race, if any. */
  raceToday: { eventId: number; league: number; done: boolean } | null;
  /** Next entered race day after today, if any. */
  nextEntry: { eventId: number; day: number; league: number } | null;
}

export function makeSnapshot(sim: RanchSim): HudSnapshot {
  const state = sim.state;
  const horse = sim.featured();
  const stats = {} as Record<StatId, StatView>;
  for (const stat of ['speed', 'stamina', 'accel', 'heart'] as StatId[]) {
    const level = horse?.stats[stat] ?? 0;
    stats[stat] = {
      level,
      xp: horse?.xp[stat] ?? 0,
      xpToNext: xpToNext(Math.max(1, level)),
      cap: sim.statCap(stat),
    };
  }
  const todayEntry = state.entries.find((entry) => eventDay(entry.eventId) === state.meta.day) ?? null;
  const future = state.entries
    .filter((entry) => eventDay(entry.eventId) > state.meta.day)
    .sort((a, b) => eventDay(a.eventId) - eventDay(b.eventId));
  const nextEntry = future[0] ?? null;
  return {
    day: state.meta.day,
    season: seasonOf(state.meta.day),
    seasonDay: seasonDayOf(state.meta.day),
    moneyCents: state.economy.moneyCents,
    energy: state.energy.current,
    energyMax: state.energy.max,
    feedPlanName: FEED_PLANS[state.economy.feedPlan].name,
    hasHorse: horse !== null,
    horseName: horse?.name ?? '(no racer)',
    horseAge: horse ? ageOf(horse, state.meta.day) : null,
    fatigue: horse?.fatigue ?? 0,
    fatigueVisible: state.vetLevel >= 1,
    injuryDays: horse?.injury?.daysLeft ?? 0,
    stats,
    winsByLeague: [...state.record.winsByLeague],
    starts: state.record.starts,
    earningsCents: state.record.earningsCents,
    raceToday:
      todayEntry === null
        ? null
        : { eventId: todayEntry.eventId, league: eventLeague(todayEntry.eventId), done: false },
    nextEntry:
      nextEntry === null
        ? null
        : {
            eventId: nextEntry.eventId,
            day: eventDay(nextEntry.eventId),
            league: eventLeague(nextEntry.eventId),
          },
  };
}

export interface RaceCard {
  eventId: number;
  day: number;
  league: number;
  leagueName: string;
  className: string;
  going: GoingId;
  surface: SurfaceId;
  /** Injury gamble, percent — 0 means genuinely safe. */
  riskPct: number;
  distanceM: number;
  entryFeeCents: number;
  firstPurseCents: number;
  entered: boolean;
  enteredHorseId: number | null;
  enteredHorseName: string | null;
  locked: boolean;
  isToday: boolean;
  /** False when locked, already entered elsewhere that day, unaffordable… */
  canEnter: boolean;
  canWithdraw: boolean;
  /** Today's entered, un-run race: time to ride. */
  canRide: boolean;
}

/** The Notice Board's data: every race in the entry window. */
export function scheduleWindow(sim: RanchSim, horizonDays = ENTRY_HORIZON_DAYS): RaceCard[] {
  const state = sim.state;
  const cards: RaceCard[] = [];
  for (let day = state.meta.day; day <= state.meta.day + horizonDays; day += 1) {
    for (const league of racesOnDay(day)) {
      const id = eventId(day, league);
      const economy = LEAGUE_ECONOMY[league]!;
      const def = LEAGUE_DEFS[league]!;
      const raceClass = raceClassOf(day, league);
      const going = goingOf(state.meta.seed, day, league);
      const entry = sim.entryForEvent(id);
      const entered = entry !== null;
      const enteredHorse = entry ? sim.horseById(entry.horseId) : null;
      const locked = !sim.leagueUnlocked(league);
      const isToday = day === state.meta.day;
      const featured = sim.featured();
      const horseTaken = featured
        ? state.entries.some(
            (candidate) =>
              candidate.horseId === featured.id && eventDay(candidate.eventId) === day,
          )
        : false;
      const canEnter =
        !entered &&
        !locked &&
        !horseTaken &&
        featured !== null &&
        !featured.injury &&
        economy.entryFeeCents <= state.economy.moneyCents &&
        !(isToday && (sim.hasRacedToday(featured.id) || sim.trainedToday(featured.id)));
      cards.push({
        eventId: id,
        day,
        league,
        leagueName: def.name,
        className: raceClass.name,
        going: going.going,
        surface: going.surface,
        riskPct: sim.injuryRiskPct(entry?.horseId ?? featured?.id ?? null),
        distanceM: raceClass.distanceM,
        entryFeeCents: economy.entryFeeCents,
        firstPurseCents: economy.purseCents[0],
        entered,
        enteredHorseId: entry?.horseId ?? null,
        enteredHorseName: enteredHorse?.name ?? null,
        locked,
        isToday,
        canEnter,
        canWithdraw: entered && day > state.meta.day,
        canRide: entered && isToday && Boolean(enteredHorse) && !sim.hasRacedToday(entry!.horseId),
      });
    }
  }
  return cards;
}

/** Deterministic without consuming sim RNG state, so the command tape
 * alone replays a career. (Training seeds are drawn inside the sim by
 * startTraining.) */
export function raceSeed(sim: RanchSim, forEventId: number): number {
  return hashFnv1a(`${sim.state.meta.seed}:race:${forEventId}`);
}
